import { useQuery } from '@apollo/client';
import { Button, MenuItem } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { BaseDialog } from '../../../../base/components/BaseDialog';
import { Flex } from '../../../../base/components/Flex';
import FormikDateTimePicker from '../../../../base/components/form/FormikDateTimePicker';
import FormikForm from '../../../../base/components/form/FormikForm';
import FormikSelect from '../../../../base/components/form/FormikSelect';
import { FormikSelectMultipleWithContext } from '../../../../base/components/form/FormikSelectMultipleWithContext';
import FormikTextField from '../../../../base/components/form/FormikTextField';
import {
  CompanyFilesDocument,
  CompanyFileType,
  CompanyQrLoginType,
  CompanyQrScenesDocument,
  CreateCompanyQrDocument,
  EditCompanyQrFileInput,
  GetVrDevicesDocument,
} from '../../../../graphql/graphql-operations';
import { useMutationWithSnackbar } from '../../../../hooks/useMutationWithSnackbar';
import {
  TFunctionPrefixed,
  useTranslationPrefix,
} from '../../../../hooks/useTranslationPrefix';

interface FormData {
  id: string;
  vrDeviceId: string;
  backupVrDeviceId: string;
  qrSceneFilter: string[];
  image_1: string;
  image_2: string;
  additionalParams: string;
  validTo: Date | null;
  loginType: CompanyQrLoginType;
}

interface Props {
  companyId: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CompanyQrDialog = ({ open, companyId, onClose, onSuccess }: Props) => {
  const { _t, t } = useTranslationPrefix('CompanyQrsPage');

  const { data: devicesData } = useQuery(GetVrDevicesDocument, {
    variables: {
      companyIdOptionalInput: {
        companyId,
      },
    },
  });
  const devices = devicesData?.vrDevices || [];

  const { data: imagesData } = useQuery(CompanyFilesDocument, {
    variables: {
      input: {
        companyId: companyId,
        type: CompanyFileType.IMAGE,
      },
    },
  });
  const images = imagesData?.companyFiles || [];

  const { data: qrScenesData } = useQuery(CompanyQrScenesDocument, {
    variables: {
      input: {
        companyId: companyId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const qrScenes = qrScenesData?.companyQrScenes || [];

  const [createMutation] = useMutationWithSnackbar(
    'CompanyQrsPage.createMutation',
    CreateCompanyQrDocument,
    {
      onCompleted: () => {
        onSuccess?.();
      },
    },
  );

  const handleSubmit = async (
    values: FormData,
    helpers: FormikHelpers<FormData>,
  ) => {
    const files: EditCompanyQrFileInput[] = [];
    if (values.image_1) {
      files.push({ paramName: 'image_1', fileId: values.image_1 });
    }
    if (values.image_2) {
      files.push({ paramName: 'image_2', fileId: values.image_2 });
    }

    await createMutation({
      variables: {
        input: {
          id: values.id,
          companyId: companyId,
          vrDeviceId: values.vrDeviceId,
          backupVrDeviceId: values.backupVrDeviceId || null,
          validTo: values.validTo?.toISOString() || null,
          additionalParams: values.additionalParams,
          files: files,
          qrSceneFilter: values.qrSceneFilter,
          loginType: values.loginType,
        },
      },
    });
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={_t('dialog.title', 'Add new QR code')}
      disableEnforceFocus
    >
      <>
        <Formik<FormData>
          initialValues={{
            id: '',
            vrDeviceId: '',
            backupVrDeviceId: '',
            additionalParams: `{"anneName": "01"}`,
            validTo: null,
            image_1: '',
            image_2: '',
            qrSceneFilter: [],
            loginType: CompanyQrLoginType.EMAIL,
          }}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={getValidationSchema(_t)}
        >
          {({ isSubmitting }) => (
            <FormikForm>
              <Flex flexDirection="column" gap={3}>
                <FormikTextField
                  fullWidth
                  name="id"
                  label={_t('dialog.id', 'URL of QR code')}
                />
                <FormikSelect
                  fullWidth
                  name="vrDeviceId"
                  label={_t('dialog.vrDeviceId', 'VR Device')}
                >
                  {devices?.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </FormikSelect>
                <FormikSelect
                  fullWidth
                  name="backupVrDeviceId"
                  label={_t('dialog.backupVrDeviceId', 'Backup VR Device')}
                >
                  <MenuItem value={''}>{_t('noDevice', 'No device')}</MenuItem>
                  {devices?.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </FormikSelect>
                <FormikSelectMultipleWithContext
                  name="qrSceneFilter"
                  fullWidth
                  label={_t('dialog.qrSceneFilter', 'Qr Scenes')}
                  multiple
                  color="primary"
                  sx={{ width: '100%' }}
                  options={qrScenes.map((scene) => ({
                    id: scene.id,
                    name: scene.scene.name_t,
                  }))}
                />
                <FormikSelect
                  fullWidth
                  name="loginType"
                  label={_t('dialog.loginType', 'Login type')}
                >
                  <MenuItem value={CompanyQrLoginType.NOT_SET}>
                    {t(
                      `enum.CompanyQrLoginType.${CompanyQrLoginType.NOT_SET}`,
                      'Both',
                    )}
                  </MenuItem>
                  <MenuItem value={CompanyQrLoginType.ANNONYMOUS}>
                    {t(
                      `enum.CompanyQrLoginType.${CompanyQrLoginType.ANNONYMOUS}`,
                      'Annonymous',
                    )}
                  </MenuItem>
                  <MenuItem value={CompanyQrLoginType.EMAIL}>
                    {t(
                      `enum.CompanyQrLoginType.${CompanyQrLoginType.EMAIL}`,
                      'E-mail',
                    )}
                  </MenuItem>
                </FormikSelect>

                <FormikSelect fullWidth name="image_1" label="image_1">
                  <MenuItem value={''}>
                    {_t('noImage', 'Without image')}
                  </MenuItem>
                  {images?.map((image) => (
                    <MenuItem key={image.id} value={image.id}>
                      {image.name}
                    </MenuItem>
                  ))}
                </FormikSelect>

                <FormikSelect fullWidth name="image_2" label="image_2">
                  <MenuItem value={''}>
                    {_t('noImage', 'Without image')}
                  </MenuItem>

                  {images?.map((image) => (
                    <MenuItem key={image.id} value={image.id}>
                      {image.name}
                    </MenuItem>
                  ))}
                </FormikSelect>
                <FormikDateTimePicker
                  name="validTo"
                  label={_t('dialog.validTo', 'Valid to')}
                  disablePast={true}
                />
                <FormikTextField
                  fullWidth
                  name="additionalParams"
                  label={_t(
                    'dialog.additionalParams',
                    'Additional params (json)',
                  )}
                  multiline
                  minRows={3}
                  maxRows={8}
                />

                <Flex
                  justifyContent="center"
                  mt={1}
                  gap={3}
                  flexDirection={{ xs: 'column-reverse', md: 'row' }}
                >
                  <Button
                    id="create-playlist-folder-cancel"
                    onClick={onClose}
                    color="primary"
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    {_t('dialog.cancel', 'Cancel')}
                  </Button>
                  <Button
                    id="create-playlist-folder-confirm"
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {_t('dialog.submit', 'Save')}
                  </Button>
                </Flex>
              </Flex>
            </FormikForm>
          )}
        </Formik>
      </>
    </BaseDialog>
  );
};

export default CompanyQrDialog;

const getValidationSchema = (_t: TFunctionPrefixed) =>
  Yup.object({
    vrDeviceId: Yup.string().required(
      _t('dialog.vrDeviceIdRequired', 'Select VR Device'),
    ),
    additionalParams: Yup.string()
      .required(
        _t('dialog.additionalParamsRequired', 'Additional params are required'),
      )
      .test(
        'json',
        _t('dialog.additionalParamsInvalid', 'Invalid json'),
        (value) => {
          try {
            JSON.parse(value || '');
            return true;
          } catch (error) {
            return false;
          }
        },
      ),
  });
